import { useState, useEffect, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Capacitor } from '@capacitor/core';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowDown, ArrowUp, Barcode, Camera, ChevronDown, GripVertical, Image as ImageIcon, Loader2, Minus, Package, Plus, ShoppingBag, Sparkles, Trash2, Wrench, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useCamera, dataUrlToFile } from '@/hooks/useCamera';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getCachedCatalog } from '@/lib/offlineDataCache';
import type { Product } from '@/types/database';
import { ItemPhotoModal } from '@/components/quotes/ItemPhotoModal';
import { ImageCropModal } from '@/components/ui/ImageCropModal';
import { StorageImage } from '@/components/ui/StorageImage';
import { formatCostInput, parseCostInput, productUnitCost } from '@/lib/cost';
import { suggestInternalCode } from '@/lib/productCodes';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { searchServicos, getCorrelacaoByLC116 } from '@/lib/fiscalData';
import { quoteItemGross, quoteItemLineTotal } from '@/lib/quoteTotals';
import {
  MIN_QUANTITY,
  formatQuantityInput,
  parseQuantityInput,
  quantityInputExceedsPrecision,
  roundQuantity,
} from '@/lib/quantity';
import {
  PRODUCT_UNIT_GROUPS,
  SERVICE_UNIT_GROUPS,
  normalizeUnit,
  unitGroupsIncluding,
} from '@/lib/units';
import { cn } from '@/lib/utils';
import { ProTag } from '@/components/ui/pro-tag';
import { useCoarsePointer } from '@/hooks/useCoarsePointer';
import { MoneyInput, MoneyTextInput } from '@/components/ui/money-input';
import { DiscountField } from '@/components/quotes/DiscountField';
import { numberToMaskedBRL } from '@/lib/currencyInput';
import { VariantDraftsSection } from '@/components/catalog/VariantDraftsSection';
import { BundleMaterialsSection } from '@/components/catalog/BundleMaterialsSection';
import type { VariantDraft } from '@/lib/variants';
import type { BundleItemRow } from '@/lib/productBundles';
import {
  PRODUCT_STOCK_MODE_INHERIT,
  PRODUCT_STOCK_MODE_OPTIONS,
  productStockModeFromSelect,
  type ProductStockDeductionMode,
} from '@/lib/stockSettings';

/**
 * As listas de unidade agora vivem em `src/lib/units.ts` — o app tinha QUATRO
 * cópias divergentes delas (aqui, CatalogPage, CatalogSelector, QuoteItemCard)
 * e mais um mapa no PDF e outro no XLSX. Reexportadas com o nome antigo para
 * não quebrar quem já importava daqui.
 */
export const serviceUnitGroups = SERVICE_UNIT_GROUPS;
export const productUnitGroups = PRODUCT_UNIT_GROUPS;

// Flatten for validation
const allServiceUnits = serviceUnitGroups.flatMap(g => g.options.map(o => o.value));
const allProductUnits = productUnitGroups.flatMap(g => g.options.map(o => o.value));

const L_CODE = ["0001101", "0011001", "0010011", "0111101", "0100011", "0110001", "0101111", "0111011", "0110111", "0001011"];
const G_CODE = ["0100111", "0110011", "0011011", "0100001", "0011101", "0111001", "0000101", "0010001", "0001001", "0010111"];
const R_CODE = ["1110010", "1100110", "1101100", "1000010", "1011100", "1001110", "1010000", "1000100", "1001000", "1110100"];
const PARITY = ["LLLLLL", "LLGLGG", "LLGGLG", "LLGGGL", "LGLLGG", "LGGLLG", "LGGGLL", "LGLGLG", "LGLGGL", "LGGLGL"];

export function BarcodeSVG({ value, width = 100, height = 18 }: { value: string; width?: number; height?: number }) {
  const isNumeric = /^\d+$/.test(value);
  const isValidLength = value.length === 12 || value.length === 13;

  let binary = "";
  if (isNumeric && isValidLength) {
    const clean = value.length === 12 ? value + "0" : value;
    const firstDigit = parseInt(clean[0]);
    const parity = PARITY[firstDigit] || PARITY[0];

    binary = "101"; // Left guard
    for (let i = 1; i <= 6; i++) {
      const digit = parseInt(clean[i]);
      binary += parity[i - 1] === 'L' ? L_CODE[digit] : G_CODE[digit];
    }
    binary += "01010"; // Middle guard
    for (let i = 7; i <= 12; i++) {
      const digit = parseInt(clean[i]);
      binary += R_CODE[digit];
    }
    binary += "101"; // Right guard
  } else {
    // Deterministic mock pattern for custom codes
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = (hash << 5) - hash + value.charCodeAt(i);
      hash |= 0;
    }
    const seed = Math.abs(hash);
    binary = "101";
    for (let i = 0; i < 89; i++) {
      binary += ((seed >> (i % 30)) & 1) ? "1" : "0";
    }
    binary += "101";
  }

  const totalBars = binary.length;
  const barWidth = width / totalBars;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="text-foreground">
      {binary.split("").map((char, index) => {
        if (char === "1") {
          return (
            <rect
              key={index}
              x={index * barWidth}
              y={0}
              width={barWidth + 0.05}
              height={height}
              fill="currentColor"
            />
          );
        }
        return null;
      })}
    </svg>
  );
}

export interface QuoteItem {
  id: string;
  name: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount?: number;
  discount_type?: 'value' | 'percent';
  image_url?: string | null;
  image_url_2?: string | null;
  image_url_3?: string | null;
  saveToCategory?: boolean;
  source?: 'manual' | 'catalog';
  itemType?: 'service' | 'product';
  unit?: string;
  /** Product ID from catalog - used to identify items that came from catalog */
  product_id?: string;
  /**
   * SNAPSHOT do custo do produto no momento em que o item entrou no documento
   * (`products.cost`). Não aparece na tela — é dado de margem/CMV, gravado em
   * `quote_items.unit_cost`. Preenchido ao adicionar do catálogo/scanner/PDV e
   * preservado ao carregar um orçamento salvo, para que reeditar um documento
   * antigo NÃO reescreva o custo histórico dele com o custo de hoje.
   * `null`/ausente = item avulso ou produto sem custo informado.
   */
  unit_cost?: number | null;
  /** Código de barras (GTIN/EAN/Code128) lido pelo scanner ou herdado do catálogo. */
  barcode?: string;
  /**
   * Quando preenchido, esta linha foi MATERIALIZADA a partir de uma despesa
   * cobrável (`finance_entries.id`). O dono do valor é o lançamento do
   * financeiro, então a linha é somente leitura aqui — e o campo precisa
   * atravessar o formulário para sobreviver ao ciclo apaga-e-reinsere do save.
   * Ver `src/lib/billableExpense.ts`.
   */
  finance_entry_id?: string | null;
  category?: string | null;
  internal_code?: string | null;
  lc116_item?: string | null;
  iss_aliquota?: number | null;
  iss_retido?: boolean;
  nbs?: string | null;
  cclasstrib?: string | null;
  track_stock?: boolean;
  min_stock?: number | null;
  /**
   * Sobreposição da baixa automática de estoque para ESTE produto. `null` =
   * herdar a configuração da empresa — ausência é um terceiro estado, não `off`
   * (#97).
   */
  stock_deduction_mode?: ProductStockDeductionMode;
  /**
   * Rascunho das VARIAÇÕES (P/M/G) do produto que o "salvar este item no meu
   * catálogo" vai criar. Não é coluna de `quote_items`: existe só enquanto o
   * cadastro está sendo montado, e some assim que o produto nasce.
   */
  catalogVariants?: VariantDraft[] | null;
  /** Rascunho dos MATERIAIS INCLUSOS (kit) do serviço. Mesma natureza acima. */
  catalogBundle?: BundleItemRow[] | null;
}

/**
 * O que o card precisa saber sobre o catálogo para editar variações e materiais.
 * Vem do `QuoteForm`, que já carrega as duas listas (cache primeiro, servidor
 * depois) — buscá-las de novo aqui daria uma consulta por item na tela.
 */
export interface QuoteItemCatalogContext {
  /** Catálogo plano (pais e variações). */
  products: Product[];
  /** Ids que TÊM variações — não são vendáveis, logo não servem de material. */
  parentsWithVariants: Set<string>;
  /** Ids que já são kit (um nível só). */
  kitParentIds: Set<string>;
  /** Ids que já são material de algum kit. */
  materialIds: Set<string>;
  isOnline: boolean;
  companyId: string | null;
  /** Plano libera variações? `false` só desenha o selo PRO. */
  variantsAllowed: boolean;
  /** Portão de plano — `false` quando o paywall foi aberto. */
  requireVariants: () => boolean;
}

interface SortableQuoteItemCardProps {
  item: QuoteItem;
  onUpdate: (id: string, field: keyof QuoteItem | Partial<QuoteItem>, value?: any) => void;
  onRemove: (id: string) => void;
  onImageUpload: (id: string, file: File, slot: 'image_url' | 'image_url_2' | 'image_url_3') => void;
  onImageRemove: (id: string, slot: 'image_url' | 'image_url_2' | 'image_url_3') => void;
  /**
   * Slots com upload EM CURSO, na forma `${itemId}:${slot}`. Quem manda é o
   * formulário, que é quem chama o Storage — o card não tem como saber sozinho.
   */
  uploadingSlots?: ReadonlySet<string>;
  disabled?: boolean;
  canRemove?: boolean;
  showSaveToCatalog?: boolean;
  showTypeAndUnit?: boolean;
  canUploadPhoto?: boolean;
  isProUser?: boolean;
  /** Called when non-PRO user tries to upload photo - opens upgrade modal */
  onProFeatureClick?: () => void;
  /** Posição na lista (0-based) — rotula o item e desabilita as setas nas pontas. */
  index?: number;
  /** Total de itens — sem ele não dá para saber onde estão as pontas da lista. */
  count?: number;
  /**
   * Reordenar por SETAS: é o caminho do TOQUE. A alça de arrasto só aparece com
   * mouse, porque arrastar com o dedo numa página que rola disputa com o scroll.
   */
  onMove?: (direction: -1 | 1) => void;
  /**
   * Catálogo + plano, para os blocos de variações e materiais. Ausente (o
   * visitante, por exemplo) esconde os dois — nunca desenha um bloco que não
   * teria como salvar.
   */
  catalogContext?: QuoteItemCatalogContext;
}

export function SortableQuoteItemCard({
  item,
  onUpdate,
  onRemove,
  onImageUpload,
  onImageRemove,
  uploadingSlots,
  disabled,
  canRemove = true,
  showSaveToCatalog = false,
  showTypeAndUnit = false,
  canUploadPhoto = true,
  isProUser = false,
  onProFeatureClick,
  index,
  count,
  onMove,
  catalogContext,
}: SortableQuoteItemCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const { capturePhoto, isCapturing } = useCamera();
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [rawPhotoUrl, setRawPhotoUrl] = useState<string | null>(null);
  const [photoModalUrl, setPhotoModalUrl] = useState<string>('');
  const [activeUploadSlot, setActiveUploadSlot] = useState<'image_url' | 'image_url_2' | 'image_url_3' | null>(null);
  const [isEditingBarcode, setIsEditingBarcode] = useState(false);
  const [tempBarcode, setTempBarcode] = useState(() => item.barcode || '');
  const { user } = useAuth();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [itemLc116Query, setItemLc116Query] = useState('');
  const itemLc116Results = useMemo(() => {
    if (!itemLc116Query.trim()) return [];
    return searchServicos(itemLc116Query);
  }, [itemLc116Query]);

  const catalogProducts = useMemo(() => {
    if (!user) return [];
    return getCachedCatalog(user.id) || [];
  }, [user]);

  const productSuggestions = useMemo(() => {
    const query = item.name.trim().toLowerCase();
    if (!query || !isInputFocused) return [];
    return catalogProducts
      .filter((p: any) => p && p.name && p.name.toLowerCase().includes(query))
      .slice(0, 5);
  }, [catalogProducts, item.name, isInputFocused]);

  useEffect(() => {
    setTempBarcode(item.barcode || '');
  }, [item.barcode]);

  // Local string state for the quantity input so the user can clear the "1"
  // while typing (the numeric model would otherwise snap back to 1 immediately).
  // Vírgula é o separador decimal em pt-BR: 1,5 kg.
  const [qtyInput, setQtyInput] = useState(() => formatQuantityInput(item.quantity));

  useEffect(() => {
    setQtyInput(formatQuantityInput(item.quantity));
  }, [item.quantity]);

  /**
   * Passo dos botões −/+: 1 quando a quantidade é inteira, e o mesmo 1 quando é
   * fracionária (1,5 → 2,5 → 3,5). Um passo fracionário "esperto" (0,5/0,1)
   * chutaria a granularidade errada — o teclado continua sendo o caminho para
   * qualquer fração, e os botões seguem servindo para "mais um".
   */
  const changeQuantity = (next: number) => {
    // `roundQuantity` é o MESMO helper do PDV (3 casas): a quantidade de um item
    // do orçamento e a do carrinho gravam na mesma coluna `quote_items.quantity`
    // e não podem arredondar diferente.
    const clamped = roundQuantity(Math.max(MIN_QUANTITY, next));
    onUpdate(item.id, 'quantity', clamped);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const discountValue = item.discount || 0;
  const discountType = item.discount_type || 'value';
  // Quarta cópia da fórmula de desconto por item que existia no app — agora vem
  // da fonte única (src/lib/quoteTotals.ts), a mesma que o rodapé do formulário,
  // o PDF e todos os saves usam. Sem isso, o total do card podia divergir do
  // total do documento por arredondamento.
  const total = quoteItemLineTotal(item);
  const discountAmount = Math.max(0, quoteItemGross(item) - total);
  const isFromCatalog = item.source === 'catalog';

  // Texto do campo de custo (só existe junto do "salvar no catálogo"). Estado
  // local porque o usuário digita "12," no meio do caminho, e converter a cada
  // tecla devolveria o campo formatado embaixo do cursor.
  const [costText, setCostText] = useState(() => formatCostInput(item.unit_cost));
  const canShowSaveCheckbox = showSaveToCatalog && !isFromCatalog;
  const canShowTypeAndUnit = (showTypeAndUnit || canShowSaveCheckbox) && !isFromCatalog;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const { scan: scanBarcode } = useBarcodeScanner();

  const handleScanAgain = async () => {
    try {
      const code = await scanBarcode();
      if (code) {
        onUpdate(item.id, 'barcode', code.trim() || undefined);
        toast.success('Código de barras lido com sucesso!');
      }
    } catch (err) {
      console.error('Error scanning barcode:', err);
      toast.error('Erro ao ler código de barras.');
    }
  };

  const handleCapturePhotoForSlot = async (slot: 'image_url' | 'image_url_2' | 'image_url_3') => {
    if (!isProUser && slot !== 'image_url') {
      if (onProFeatureClick) {
        onProFeatureClick();
      }
      return;
    }

    setActiveUploadSlot(slot);
    const result = await capturePhoto({
      quality: 85,
      source: 'prompt',
    });

    if (result?.dataUrl) {
      setRawPhotoUrl(result.dataUrl);
      setShowCropModal(true);
    } else {
      setActiveUploadSlot(null);
    }
  };

  const handleCropComplete = (croppedImageUrl: string) => {
    if (activeUploadSlot) {
      const file = dataUrlToFile(croppedImageUrl, `item-${item.id}-${activeUploadSlot}.jpg`);
      onImageUpload(item.id, file, activeUploadSlot);
      setRawPhotoUrl(null);
      setActiveUploadSlot(null);
    }
  };

  const handleCropCancel = () => {
    setRawPhotoUrl(null);
    setActiveUploadSlot(null);
  };

  /**
   * Arrastar só com MOUSE. O critério é o PONTEIRO, não a plataforma: o teste
   * anterior (`platform !== 'android'`) deixava a alça no navegador mobile e no
   * iOS, onde ela disputa com o scroll da página.
   */
  const coarsePointer = useCoarsePointer();
  const showDragHandle = !disabled && !coarsePointer;
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <div ref={setNodeRef} style={style}>
      {/*
        Cartão-BILHETE: faixa da cor do tipo à esquerda, nome em largura cheia e um
        "canhoto" picotado com o total da linha. O que manda no layout foi MEDIDO:
        com o total ocupando uma coluna de altura cheia sobravam 114px para o nome
        em 360px de tela — estreito demais para o nome de um serviço real. O total
        virou um bloco baixo na 2ª linha e o nome passou a ~180px.
      */}
      <div
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm',
          isDragging && 'shadow-lg',
        )}
      >
        <span
          className={cn(
            'absolute inset-y-0 left-0 w-1.5',
            item.itemType === 'product' ? 'bg-amber-500' : 'bg-[#2f6fae]',
          )}
        />

        {/* Alça de arrasto — só com MOUSE. No toque, reordena pelas setas da 3ª
            linha: arrastar com o dedo numa página que rola disputa com o scroll. */}
        {showDragHandle && (
          <div
            {...attributes}
            {...listeners}
            className="absolute bottom-0 left-1.5 top-0 flex w-7 cursor-grab items-center justify-center bg-muted/50 transition-colors hover:bg-muted active:cursor-grabbing"
            aria-label={`Arrastar item ${(index ?? 0) + 1}`}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        )}

        <div className={cn('space-y-2 p-3', showDragHandle ? 'pl-[38px]' : 'pl-4')}>
          {/* Linha vinda de uma despesa cobrável: o valor é do lançamento do
              financeiro, não deste formulário. Sem este aviso, uma linha
              inexplicavelmente travada pareceria bug. */}
          {item.finance_entry_id && (
            <p className="rounded-md bg-muted px-2 py-1 text-[11px] text-muted-foreground">
              Despesa cobrável — edite o valor ou remova pelo Financeiro.
            </p>
          )}

          {/* LINHA 1 — tipo + nome, até a borda do card */}
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                item.itemType === 'product'
                  ? 'bg-amber-500/15 text-amber-600'
                  : 'bg-[#2f6fae]/15 text-[#2f6fae]',
              )}
            >
              {item.itemType === 'product' ? (
                <ShoppingBag className="h-3.5 w-3.5" />
              ) : (
                <Wrench className="h-3.5 w-3.5" />
              )}
            </span>
            <div className="relative">
              <Input
                placeholder={
                  item.itemType === 'product'
                    ? 'Nome do produto *'
                    : item.itemType === 'service'
                      ? 'Nome do serviço *'
                      : 'Nome do serviço/produto *'
                }
                value={item.name}
                onChange={(e) => onUpdate(item.id, 'name', e.target.value)}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setTimeout(() => setIsInputFocused(false), 150)}
                disabled={disabled}
                className="text-base font-semibold border-0 border-b rounded-none px-0 pr-10 focus-visible:ring-0 focus-visible:border-primary w-full"
                autoComplete="off"
              />
              {/* Product suggestions dropdown */}
              {isInputFocused && productSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-60 overflow-y-auto rounded-md border bg-popover shadow-md">
                  {productSuggestions.map((product: any) => (
                    <button
                      key={product.id}
                      type="button"
                      onMouseDown={async (e) => {
                        e.preventDefault();

                        // Map the selected product to the item
                        const updates: Partial<QuoteItem> = {
                          name: product.name,
                          description: product.description || '',
                          unit_price: product.price,
                          itemType: product.type === 'product' ? 'product' : 'service',
                          unit: product.unit || 'un',
                          product_id: product.id,
                          barcode: product.barcode || undefined,
                          unit_cost: productUnitCost(product),
                          source: 'catalog',
                          category: product.category || null,
                          internal_code: product.internal_code || null,
                          track_stock: product.track_stock || false,
                          min_stock: product.min_stock || null,
                          lc116_item: product.lc116_item || null,
                          iss_aliquota: product.iss_aliquota || null,
                          iss_retido: product.iss_retido === true,
                          nbs: product.nbs || null,
                          cclasstrib: product.cclasstrib || null,
                        };

                        // Fetch product photos from product_photos if online
                        // or fallback to product.image_url if present
                        if (product.id) {
                          try {
                            const { data: photosData } = await supabase
                              .from('product_photos')
                              .select('image_url, position')
                              .eq('product_id', product.id)
                              .order('position');
                            if (photosData && photosData.length > 0) {
                              const sortedPhotos = [...photosData].sort((a, b) => a.position - b.position);
                              updates.image_url = sortedPhotos[0]?.image_url || undefined;
                              updates.image_url_2 = sortedPhotos[1]?.image_url || undefined;
                              updates.image_url_3 = sortedPhotos[2]?.image_url || undefined;
                            } else if (product.image_url) {
                              updates.image_url = product.image_url;
                            }
                          } catch (err) {
                            console.error('Error fetching photos for autocomplete:', err);
                            if (product.image_url) {
                              updates.image_url = product.image_url;
                            }
                          }
                        }

                        onUpdate(item.id, updates);
                        setIsInputFocused(false);
                      }}
                      className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-accent focus:bg-accent focus:outline-none"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        {product.name}
                      </span>
                      <span className="flex flex-wrap gap-x-3 gap-y-0.5 pl-5 text-xs text-muted-foreground">
                        {product.price !== undefined && (
                          <span>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(product.price)}
                          </span>
                        )}
                        {product.barcode && (
                          <span className="font-mono">{product.barcode}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/*
            LINHA 2 — quantidade, preço unitário e total NA MESMA LINHA. As larguras
            são medidas: sobram ~298px úteis no card em 360px, então o passo-a-passo
            fica em 82px fixos, o Total em 76px, e o preço absorve o resto.
          */}
          <div className="flex items-center gap-1.5">
            {/* ALTURA PADRÃO no container (h-10): com a altura nos botões, a
                borda do agrupador somava 2px e o passo-a-passo ficava mais baixo
                que o campo de preço ao lado — o desalinhamento visível na linha. */}
            <div className="flex h-10 shrink-0 items-center rounded-xl border border-border bg-background">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-full w-6 rounded-l-xl rounded-r-none"
                onClick={() => changeQuantity(item.quantity - 1)}
                disabled={disabled || item.quantity <= MIN_QUANTITY}
                aria-label="Diminuir quantidade"
              >
                <Minus className="h-3 w-3" />
              </Button>
              <Input
                type="text"
                inputMode="decimal"
                value={qtyInput}
                onChange={(e) => {
                  const raw = e.target.value;
                  const parsed = parseQuantityInput(raw);
                  // O modelo guarda no máximo 3 casas. Deixar o texto cru com
                  // mais do que isso faz a tela mostrar um número que o app não
                  // guardou ("1,0001" com 1 no documento) — e, quando o
                  // arredondamento cai no valor que o item JÁ tinha, o efeito de
                  // sincronia não dispara e a mentira fica na tela. Ver
                  // `quantityInputExceedsPrecision`.
                  setQtyInput(
                    parsed.ok && parsed.value != null && quantityInputExceedsPrecision(raw)
                      ? formatQuantityInput(parsed.value)
                      : raw,
                  );
                  if (parsed.ok && parsed.value != null) {
                    onUpdate(item.id, 'quantity', parsed.value);
                  }
                }}
                onBlur={() => {
                  const parsed = parseQuantityInput(qtyInput);
                  if (!parsed.ok || parsed.value == null) {
                    setQtyInput('1');
                    onUpdate(item.id, 'quantity', 1);
                    return;
                  }
                  setQtyInput(formatQuantityInput(parsed.value));
                }}
                disabled={disabled}
                className="h-full w-8 rounded-none border-0 bg-transparent px-0 text-center text-sm"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-full w-6 rounded-l-none rounded-r-xl"
                onClick={() => changeQuantity(item.quantity + 1)}
                disabled={disabled}
                aria-label="Aumentar quantidade"
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            <MoneyInput
              compact
              value={item.unit_price}
              onCommit={(v) => onUpdate(item.id, 'unit_price', v)}
              disabled={disabled}
              aria-label="Preço unitário"
              className="min-w-0 flex-1"
            />
            {/* Canhoto picotado. O "R$" fica no RÓTULO: medido, `R$ 1.500,00` a 13px
                estourava os 66px úteis da coluna, e valor cortado num total é o pior
                lugar possível para faltar espaço. */}
            <div className="w-[76px] shrink-0 border-l-2 border-dashed border-border pl-2 text-right">
              <span className="block text-[9px] uppercase leading-none tracking-wide text-muted-foreground">
                Total R$
              </span>
              <span className="block text-xs font-bold leading-tight text-primary">
                {numberToMaskedBRL(total) || '0,00'}
              </span>
            </div>
          </div>

          {disabled && discountAmount > 0 && (
            <p className="text-xs text-muted-foreground">
              Desconto aplicado: {discountType === 'percent' ? `${discountValue}%` : formatCurrency(discountAmount)}
            </p>
          )}

          {/* LINHA 3 — mais opções, reordenar (toque) e remover */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              className="flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium text-primary hover:bg-primary/10"
            >
              {detailsOpen ? 'Menos opções' : 'Mais opções'}
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', detailsOpen && 'rotate-180')} />
            </button>
            <div className="flex items-center gap-1">
              {isFromCatalog && (
                // Tag, não texto solto: o mesmo formato de pílula dos demais selos
                // do app, para "veio do catálogo" ser lido como um ESTADO da linha.
                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold leading-none text-primary">
                  está no catálogo
                </span>
              )}
              {!showDragHandle && !disabled && onMove && (count ?? 0) > 1 && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={(index ?? 0) === 0}
                    onClick={() => onMove(-1)}
                    aria-label={`Mover item ${(index ?? 0) + 1} para cima`}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={(index ?? 0) === (count ?? 1) - 1}
                    onClick={() => onMove(1)}
                    aria-label={`Mover item ${(index ?? 0) + 1} para baixo`}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
              {canRemove && !disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => onRemove(item.id)}
                  aria-label={`Remover item ${(index ?? 0) + 1}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* DETALHES — tudo o que não é nome, quantidade ou preço */}
        {detailsOpen && (
          <div
            className={cn(
              'space-y-3 border-t border-dashed border-border bg-muted/30 p-3',
              showDragHandle ? 'pl-[38px]' : 'pl-4',
            )}
          >
            {/*
              Descrição do item. A coluna `quote_items.description` já existia, já
              era gravada, já era lida e já saía no PDF — só não havia INPUT: a
              descrição só entrava se o item viesse do catálogo, e quem digitava um
              item avulso não tinha como detalhar o serviço.
              Escondida quando o formulário está em leitura e não há texto, para
              não deixar um campo vazio no orçamento já fechado.
            */}
            {(!disabled || item.description) && (
              <Textarea
                placeholder="Descrição / detalhamento (opcional)"
                value={item.description || ''}
                onChange={(e) => onUpdate(item.id, 'description', e.target.value)}
                disabled={disabled}
                rows={2}
                className="resize-none text-sm"
              />
            )}

            {/* Fotos do item: 3 slots baixos e tracejados (o quadrado grande
                empurrava o resto do bloco para fora da primeira dobra). */}
            {canUploadPhoto && (
              <div className="flex gap-2">
                {(['image_url', 'image_url_2', 'image_url_3'] as const).map((slot, index) => {
                  const imageUrl = item[slot];

                  /*
                    ENVIANDO. Vem ANTES do ramo da imagem porque o slot ainda
                    está vazio enquanto o upload acontece — era exatamente essa
                    janela (segundos numa rede de celular) em que a tela não
                    dizia nada e o usuário concluía que o app tinha engolido a
                    foto. O slot fica com a moldura da foto (borda cheia, não
                    tracejada) para já parecer ocupado.
                  */
                  if (uploadingSlots?.has(`${item.id}:${slot}`)) {
                    return (
                      <div
                        key={slot}
                        className="flex h-16 flex-1 flex-col items-center justify-center gap-1 rounded-xl border border-primary/40 bg-primary/5 text-[10px] text-muted-foreground"
                        aria-live="polite"
                      >
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        Enviando...
                      </div>
                    );
                  }

                  if (imageUrl) {
                    return (
                      <div key={slot} className="relative h-16 flex-1">
                        <div className="h-full w-full overflow-hidden rounded-xl border border-primary/40">
                          <StorageImage
                            src={imageUrl}
                            alt={`Imagem ${index + 1}`}
                            className="h-full w-full cursor-pointer object-cover"
                            onClick={() => {
                              setPhotoModalUrl(imageUrl);
                              setShowPhotoModal(true);
                            }}
                          />
                        </div>
                        {!disabled && (
                          <button
                            type="button"
                            onClick={() => onImageRemove(item.id, slot)}
                            className="absolute -right-1.5 -top-1.5 z-10 rounded-full bg-destructive p-0.5 text-destructive-foreground shadow transition-colors hover:bg-destructive/90"
                            title="Remover imagem"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    );
                  }
                  if (disabled) return <div key={slot} className="h-16 flex-1" />;
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => handleCapturePhotoForSlot(slot)}
                      disabled={isCapturing}
                      title={`Adicionar foto ${index + 1}`}
                      className="relative flex h-16 flex-1 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border text-[10px] text-muted-foreground transition-colors hover:border-primary/40"
                    >
                      <Camera className="h-4 w-4" />
                      Foto {index + 1}
                      {!isProUser && slot !== 'image_url' && (
                        <ProTag className="absolute -right-1 -top-1" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Desconto + unidade lado a lado */}
            {!disabled && (
              <div className="grid grid-cols-2 items-start gap-3">
                <div className="space-y-1.5">
                  <Label className="flex h-4 items-center text-xs font-medium">Desconto</Label>
                  <DiscountField
                    value={item.discount}
                    type={item.discount_type || 'value'}
                    onValueChange={(v) => onUpdate(item.id, 'discount', v)}
                    onTypeChange={(t) => onUpdate(item.id, 'discount_type', t)}
                    disabled={disabled}
                  />
                </div>
                {/*
                  A unidade aparece SEMPRE, inclusive no item vindo do catálogo.
                  Ela ficava escondida nesse caso (`canShowTypeAndUnit` exclui
                  item de catálogo) porque o produto "já traz a sua" — mas a
                  unidade é do ITEM DO DOCUMENTO, não do cadastro: quem vende o
                  cabo por metro numa proposta e por rolo na outra não tinha como
                  ajustar, e a coluna ficava com o valor do catálogo em silêncio.
                */}
                {!disabled && (
                  <div className="space-y-1.5">
                    <Label className="flex h-4 items-center text-xs font-medium">Unidade</Label>
                    {/* unitGroupsIncluding + normalizeUnit: um item vindo do
                        catálogo pode carregar uma unidade do vocabulário ANTIGO
                        (hour, day, service...). Com o valor fora das opções, o
                        Radix mostra o PLACEHOLDER e o usuário lê "Selecione a
                        unidade" num item que TEM unidade. Ver src/lib/units.ts. */}
                    <Select
                      value={normalizeUnit(item.unit) || ''}
                      onValueChange={(value) => onUpdate(item.id, 'unit', value)}
                    >
                      <SelectTrigger className="h-10 rounded-xl bg-background">
                        <SelectValue placeholder="Selecione a unidade" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72 bg-popover">
                        {unitGroupsIncluding(item.unit, item.itemType === 'product' ? 'product' : 'service').map((group) => (
                          <SelectGroup key={group.label}>
                            <SelectLabel className="text-xs text-muted-foreground">
                              {group.label}
                            </SelectLabel>
                            {group.options.map((u) => (
                              <SelectItem key={u.value} value={u.value}>
                                {u.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {canShowSaveCheckbox && !disabled && (
              /* CUSTO — só aparece ao marcar o checkbox. O checkbox só existe para
                 item DIGITADO À MÃO (item do catálogo já tem source: 'catalog'),
                 então sem este campo todo produto criado por aqui nasceria com
                 cost = null para sempre, e a base de margem/CMV nunca sairia do
                 zero. O mesmo valor alimenta os DOIS lados: products.cost do item
                 novo e quote_items.unit_cost desta linha. */
              <div className="space-y-2 rounded-xl border border-primary/25 bg-primary/5 p-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`save-catalog-${item.id}`}
                    checked={item.saveToCategory || false}
                    onCheckedChange={(checked) => onUpdate(item.id, 'saveToCategory', checked === true)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div
                    className="cursor-pointer text-sm leading-tight"
                    onClick={() => onUpdate(item.id, 'saveToCategory', !item.saveToCategory)}
                  >
                    Salvar este item no meu catálogo
                  </div>
                </div>
                {item.saveToCategory && (
                  <div className="space-y-1.5">
                    <Label
                      htmlFor={`item-cost-${item.id}`}
                      className="flex h-4 items-center text-xs font-medium"
                    >
                      Custo (opcional)
                    </Label>
                    {/* MASCARADO como todo campo de dinheiro do app (#157). O
                        `MoneyTextInput` (e não o `MoneyInput` numérico) porque
                        aqui o campo VAZIO significa "custo não informado", e
                        precisa continuar vazio em vez de comitar zero (#61). */}
                    <MoneyTextInput
                      id={`item-cost-${item.id}`}
                      className="w-32"
                      value={costText}
                      onChange={(masked) => {
                        setCostText(masked);
                        const parsed = parseCostInput(masked);
                        // Texto inválido não apaga o valor anterior nem grava 0:
                        // só não atualiza (o save valida de novo).
                        if (parsed.ok) onUpdate(item.id, 'unit_cost', parsed.value ?? undefined);
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Categoria + código interno, lado a lado */}
            <div className="grid grid-cols-2 items-start gap-3">
              <div className="space-y-1.5">
                <Label className="flex h-4 items-center text-xs font-medium">Categoria</Label>
                <Input
                  placeholder="Ex: Elétrica"
                  value={item.category || ''}
                  onChange={(e) => onUpdate(item.id, 'category', e.target.value)}
                  disabled={disabled}
                  className="h-10 rounded-xl bg-background"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="flex h-4 items-center text-xs font-medium">Código interno (SKU)</Label>
                <div className="flex items-start gap-1">
                  <Input
                    placeholder={item.itemType === 'product' ? 'PRD-001' : 'SRV-001'}
                    value={item.internal_code || ''}
                    onChange={(e) => onUpdate(item.id, 'internal_code', e.target.value)}
                    disabled={disabled}
                    className="h-10 min-w-0 rounded-xl bg-background"
                  />
                  {!disabled && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 shrink-0 rounded-xl"
                      aria-label="Gerar código interno"
                      title="Gerar código a partir do nome"
                      onClick={() => {
                        // Era `PRD-` + 6 caracteres aleatórios — uma SEGUNDA
                        // implementação do gerador, e a pior das duas: o prefixo
                        // dizia só o tipo, então todo produto do catálogo tinha
                        // o mesmo começo e o resto era ruído. Agora sai do nome,
                        // pela mesma função do formulário do catálogo.
                        const code = suggestInternalCode(item.name);
                        if (code) onUpdate(item.id, 'internal_code', code);
                      }}
                    >
                      <Sparkles className="h-4 w-4 text-primary" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Condicionais por tipo de item: estoque/código de barras para
                PRODUTO (cartão âmbar) e classificação fiscal para SERVIÇO
                (cartão azul) — a cor é a mesma da faixa do tipo no topo do card. */}
            {item.itemType === 'product' ? (
              <div className="space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-xs font-medium">Controlar estoque</Label>
                    <p className="text-[10px] text-muted-foreground">Monitorar entradas e saídas</p>
                  </div>
                  <Switch
                    id={`track-stock-${item.id}`}
                    checked={item.track_stock || false}
                    onCheckedChange={(checked) => onUpdate(item.id, 'track_stock', !!checked)}
                    disabled={disabled}
                  />
                </div>

                {item.track_stock && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="flex h-4 items-center text-xs font-medium">Estoque mínimo</Label>
                      <Input
                        type="number"
                        placeholder="Ex: 5"
                        value={item.min_stock !== undefined && item.min_stock !== null ? item.min_stock : ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? null : Number(e.target.value);
                          onUpdate(item.id, 'min_stock', val);
                        }}
                        disabled={disabled}
                        className="h-10 rounded-xl bg-background"
                      />
                    </div>

                    {/* Sobreposição da automação por produto — o mesmo campo do
                        formulário do catálogo, com as opções vindas da fonte
                        única. `inherit` é o rótulo do `null` (#97). */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor={`stock-mode-${item.id}`}
                        className="flex h-4 items-center text-xs font-medium"
                      >
                        Baixa deste produto
                      </Label>
                      <Select
                        value={item.stock_deduction_mode ?? PRODUCT_STOCK_MODE_INHERIT}
                        onValueChange={(value) =>
                          onUpdate(item.id, 'stock_deduction_mode', productStockModeFromSelect(value))}
                        disabled={disabled}
                      >
                        <SelectTrigger
                          id={`stock-mode-${item.id}`}
                          className="h-10 rounded-xl bg-background"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRODUCT_STOCK_MODE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground">
                        “Herdar” mantém a automação das configurações de estoque.
                      </p>
                    </div>
                  </>
                )}

                <div className="space-y-1.5">
                  <Label className="flex h-4 items-center text-xs font-medium">Código de barras</Label>
                  {isEditingBarcode ? (
                    <div className="flex items-start gap-1">
                      <Input
                        value={tempBarcode}
                        onChange={(e) => setTempBarcode(e.target.value)}
                        placeholder="Código de barras"
                        className="h-10 min-w-0 rounded-xl bg-background"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        className="h-10 shrink-0 rounded-xl px-3 text-xs"
                        onClick={() => {
                          onUpdate(item.id, 'barcode', tempBarcode || undefined);
                          setIsEditingBarcode(false);
                        }}
                      >
                        OK
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-10 shrink-0 rounded-xl px-2 text-xs"
                        onClick={() => {
                          setTempBarcode(item.barcode || '');
                          setIsEditingBarcode(false);
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-stretch gap-2">
                      <div className="flex min-h-[58px] flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-background/60 px-2 py-1.5">
                        {item.barcode ? (
                          <div className="flex w-full max-w-[140px] flex-col items-center gap-1">
                            <BarcodeSVG value={item.barcode} width={108} height={22} />
                            <span className="max-w-full truncate font-mono text-[11px] leading-none text-muted-foreground">
                              {item.barcode}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] italic text-muted-foreground">Sem código</span>
                        )}
                      </div>
                      {/* Os DOIS botões falam do estado do campo, não de um
                          roteiro fixo. "Ler novamente" num campo vazio promete
                          repetir uma leitura que nunca houve, e "Editar código"
                          ali abre um formulário para editar o nada — o usuário
                          clica, vê um campo em branco e volta. */}
                      <div className="flex w-24 shrink-0 flex-col justify-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-[26px] rounded-lg px-1 text-[10px] font-medium"
                          onClick={() => {
                            setTempBarcode(item.barcode || '');
                            setIsEditingBarcode(true);
                          }}
                          // Sem código não há o que editar. O rótulo continua o
                          // mesmo (desabilitado já diz que não dá), e a saída é
                          // o botão de baixo, que muda de texto.
                          disabled={disabled || !item.barcode}
                        >
                          Editar código
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          // `h-auto` + `whitespace-normal`: "Ler código de
                          // barras" não cabe numa linha nos 96px da coluna
                          // (medido em 360px), e com a altura travada em 26px o
                          // texto sairia cortado em vez de quebrar — a família
                          // #52. Quebrando, o botão cresce e a caixa do código
                          // ao lado acompanha, porque ela é `min-h`.
                          className="h-auto min-h-[26px] gap-1 whitespace-normal rounded-lg px-1 py-0.5 text-[10px] font-medium leading-tight"
                          onClick={handleScanAgain}
                          disabled={disabled}
                        >
                          <Barcode className="h-3 w-3 shrink-0" />
                          {item.barcode ? 'Ler novamente' : 'Ler código de barras'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3 rounded-xl border border-[#2f6fae]/30 bg-[#2f6fae]/5 p-3">
                <div className="space-y-1.5">
                  <Label className="flex h-4 items-center text-xs font-medium">Serviço (LC 116)</Label>
                  {!disabled && (
                    <div className="relative">
                      <Input
                        className="h-10 rounded-xl bg-background"
                        placeholder="Buscar por descrição ou código"
                        value={itemLc116Query}
                        onChange={(e) => setItemLc116Query(e.target.value)}
                      />
                      {itemLc116Results.length > 0 && (
                        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-44 overflow-y-auto rounded-xl border bg-popover shadow-md">
                          {itemLc116Results.map((s) => (
                            <button
                              key={s.codigo}
                              type="button"
                              className="block w-full px-3 py-1.5 text-left text-xs hover:bg-accent"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                // A correlação NBS/cClassTrib vem junto do código:
                                // escolher o serviço já classifica o item para a nota.
                                const corr = getCorrelacaoByLC116(s.codigo);
                                onUpdate(item.id, {
                                  lc116_item: s.codigo,
                                  nbs: corr?.nbs ?? null,
                                  cclasstrib: corr?.cClassTrib ?? null,
                                });
                                setItemLc116Query('');
                              }}
                            >
                              <span className="font-mono text-[10px] text-muted-foreground">{s.codigo}</span> — {s.descricao}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {item.lc116_item && (
                    <div className="flex items-center justify-between rounded-md bg-muted/60 p-1 text-xs text-muted-foreground">
                      <span>
                        Item LC116: <span className="font-mono font-semibold">{item.lc116_item}</span>
                      </span>
                      {!disabled && (
                        <button
                          type="button"
                          className="text-[10px] text-destructive hover:underline"
                          onClick={() => {
                            onUpdate(item.id, { lc116_item: null, nbs: null, cclasstrib: null });
                          }}
                        >
                          limpar
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label className="flex h-4 items-center text-xs font-medium">Alíquota ISS (%)</Label>
                  <Input
                    type="number"
                    placeholder="Ex: 5"
                    value={item.iss_aliquota !== undefined && item.iss_aliquota !== null ? item.iss_aliquota : ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? null : Number(e.target.value);
                      onUpdate(item.id, 'iss_aliquota', val);
                    }}
                    disabled={disabled}
                    className="h-10 rounded-xl bg-background"
                  />
                </div>
              </div>
            )}

            {/* ── VARIAÇÕES (produto) e MATERIAIS INCLUSOS (serviço) ──────────
                Os dois últimos campos que o card não espelhava do formulário do
                catálogo. Diferente dos vizinhos (categoria, código, estoque,
                fiscal), eles criam OUTRAS LINHAS no banco — variações são
                produtos filhos e materiais são vínculos —, então só aparecem
                com o "salvar no catálogo" marcado: sem ele não haveria produto
                a que pendurá-los, e um bloco inteiro sem efeito nenhum é pior
                do que bloco nenhum (#91). */}
            {canShowSaveCheckbox && item.saveToCategory && catalogContext && !disabled && (
              item.itemType === 'product' ? (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                  <VariantDraftsSection
                    value={item.catalogVariants ?? []}
                    onChange={(drafts) => onUpdate(item.id, 'catalogVariants', drafts)}
                    isOnline={catalogContext.isOnline}
                    allowed={catalogContext.variantsAllowed}
                    onRequirePro={catalogContext.requireVariants}
                    // O código sai do NOME (#210). Aqui não há a lista completa
                    // do catálogo para garantir unicidade global, então a
                    // sugestão pode colidir — o banco não tem UNIQUE em
                    // `internal_code`, e o usuário vê o código antes de salvar.
                    generateCode={(source) => suggestInternalCode(source)}
                    seed={{
                      price: numberToMaskedBRL(item.unit_price),
                      cost: formatCostInput(item.unit_cost),
                      barcode: item.barcode ?? '',
                      internalCode: item.internal_code ?? '',
                    }}
                    parentName={item.name}
                  />
                </div>
              ) : (
                <BundleMaterialsSection
                  // Serviço ainda não existe no catálogo: sem id de pai, o
                  // componente não tem como checar "já é material de outro kit"
                  // — e não precisa, porque um serviço recém-criado não é.
                  parentId=""
                  products={catalogContext.products}
                  parentsWithVariants={catalogContext.parentsWithVariants}
                  kitParentIds={catalogContext.kitParentIds}
                  materialIds={catalogContext.materialIds}
                  value={item.catalogBundle ?? []}
                  onChange={(rows) => onUpdate(item.id, 'catalogBundle', rows)}
                  isOnline={catalogContext.isOnline}
                  companyId={catalogContext.companyId}
                />
              )
            )}
          </div>
        )}
      </div>


      {/* Photo Modal */}
      {photoModalUrl && (
        <ItemPhotoModal
          open={showPhotoModal}
          onOpenChange={setShowPhotoModal}
          imageUrl={photoModalUrl}
          itemName={item.name}
          onChangePhoto={undefined}
          onRemovePhoto={undefined}
          isLoading={false}
        />
      )}

      {/* Crop Modal */}
      {rawPhotoUrl && (
        <ImageCropModal
          open={showCropModal}
          onOpenChange={setShowCropModal}
          imageSrc={rawPhotoUrl}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
        />
      )}
    </div>
  );
}
